import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, Building, LogOut, Menu, X, Zap, Package, Bug, Receipt, Mail, Users as UsersIcon, ToggleRight, Megaphone, Link2 } from 'lucide-react';
import BugReportButton from '../../components/BugReportButton';
import { useAppConfig } from '../../context/AppConfigContext';

const NAV = [
  { to: '/superadmin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/superadmin/brands', label: 'Brand', icon: Building },
  { to: '/superadmin/preview-links', label: 'Anteprima &amp; link', icon: Link2 },
  { to: '/superadmin/packages', label: 'Pacchetti', icon: Package },
  { to: '/superadmin/agents', label: 'Agenti', icon: UsersIcon },
  { to: '/superadmin/marketing-assets', label: 'Asset Marketing', icon: Megaphone },
  { to: '/superadmin/billing', label: 'Fatturazione', icon: Receipt },
  { to: '/superadmin/lazo-emails', label: 'Email Lazo', icon: Mail },
  { to: '/superadmin/feature-toggles', label: 'Moduli Admin', icon: ToggleRight },
  { to: '/superadmin/bug-reports', label: 'Segnalazioni', icon: Bug },
];

const LAZO_ONLY_SUPERADMIN = [
  '/superadmin/brands',
  '/superadmin/packages',
  '/superadmin/agents',
  '/superadmin/marketing-assets',
  '/superadmin/billing',
  '/superadmin/lazo-emails',
];

export default function SuperadminLayout() {
  const { adminUser, adminLogout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const appCfg = useAppConfig();
  const NAV_FILTERED = appCfg.single_tenant ? NAV.filter(n => !LAZO_ONLY_SUPERADMIN.includes(n.to)) : NAV;

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => { adminLogout(); navigate('/superadmin/login'); };

  return (
    <div className="admin-app flex min-h-dvh" style={{ background: '#0F172A' }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-[#1E293B] border-r border-[#334155] flex-col h-screen fixed left-0 top-0 z-10">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-[#334155]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D44E20] to-[#F59E0B] flex items-center justify-center shadow-lg">
              <Zap size={20} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-white text-sm">Lazo</p>
              <p className="text-[#64748B] text-[10px] font-medium">Superadmin</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4">
          <div className="space-y-1">
            {NAV_FILTERED.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`sa-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                    isActive
                      ? 'bg-[#D44E20] text-white font-medium'
                      : 'text-[#94A3B8] hover:bg-[#334155] hover:text-white'
                  }`
                }
              >
                <n.icon size={18} strokeWidth={1.5} />
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* User */}
        <div className="px-4 py-4 border-t border-[#334155]">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-[#D44E20] rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">{adminUser?.name?.charAt(0) || 'S'}</span>
            </div>
            <div>
              <p className="text-white text-xs font-semibold">{adminUser?.name}</p>
              <p className="text-[#64748B] text-[10px]">Superadmin</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="sa-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-[#64748B] hover:text-[#EF4444] hover:bg-[#334155] transition-all">
            <LogOut size={16} /> Disconnetti
          </button>
        </div>
      </aside>

      {/* Mobile */}
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 w-72 bg-[#1E293B] z-50 flex flex-col transform transition-transform duration-300 md:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-[#64748B]"><X size={18} /></button>
        {/* Same nav */}
        <div className="px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D44E20] to-[#F59E0B] flex items-center justify-center">
              <Zap size={20} className="text-white" />
            </div>
            <p className="font-bold text-white text-sm">Lazo Superadmin</p>
          </div>
        </div>
        <nav className="px-3 py-2">
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm mb-1 ${isActive ? 'bg-[#D44E20] text-white' : 'text-[#94A3B8] hover:bg-[#334155]'}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <main className="flex-1 md:ml-64 min-h-screen overflow-y-auto bg-slate-50">
        <div className="flex md:hidden items-center gap-3 px-4 py-3 bg-[#1E293B] border-b border-[#334155] sticky top-0 z-30">
          <button onClick={() => setMobileOpen(true)} className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#334155] text-[#94A3B8]">
            <Menu size={20} />
          </button>
          <span className="font-bold text-white text-sm">Lazo Superadmin</span>
        </div>
        <Outlet />
      </main>
      <BugReportButton userRole="superadmin" userName="Superadmin" brandName="Lazo" />
    </div>
  );
}
